/**
 * Live HTTP implementation of the B1.11 BusinessDashboard feature API.
 *
 * Targets the Phase E1 backend surface at `/api/biz/dashboard/snapshot`
 * (see `backend/src/api/business/biz-dashboard-routes.ts`).
 *
 * Contract alignment:
 *   The backend returns the Lovable-frozen `DashboardSnapshot` shape with
 *   pre-formatted strings (₺/% values) and minor-unit chart points. Five
 *   collections are returned as **empty arrays** in Phase E1 — those depend
 *   on domains not yet biz_-scoped:
 *     • aiInsights        — AI insight engine (Phase H)
 *     • upcomingBookings  — cross-context bookings RLS (Phase F)
 *     • recentReviews     — reviews biz_-scoping (Phase F)
 *     • occupancy         — service capacity model (Phase F)
 *     • quickActions      — static FE config; we merge defaults below
 *
 *   Quick actions are static UX wiring; we hydrate from `seeds.ts` when the
 *   server returns an empty list, keeping the action grid populated until a
 *   per-tenant override lands.
 *
 * Insight dismissal:
 *   `dismissDashboardInsight` is a no-op on the live wire today (no insight
 *   table). The mock surface is preserved so callers don't break; once the
 *   AI insight backend lands, this file gets the POST endpoint.
 *
 * @see dashboard-api-mock.ts (fixture path)
 * @see dashboard-api.ts      (mock↔live dispatcher)
 * @see backend/src/api/business/biz-dashboard-routes.ts
 */

import { bizGet } from '@/shared/api/bizClient';

import { DASHBOARD_QUICK_ACTIONS_SEED } from './seeds';
import type {
  DashboardQuickAction,
  DashboardSnapshot,
  GetDashboardSnapshotParams,
} from './types';

interface BackendSnapshot extends Omit<DashboardSnapshot, 'quickActions'> {
  readonly quickActions: readonly DashboardQuickAction[];
}

function adaptSnapshot(snap: BackendSnapshot): DashboardSnapshot {
  // Phase E1 backend returns quickActions: []. Until per-tenant overrides
  // land we hydrate from the static seed so the action grid stays populated.
  const quickActions =
    snap.quickActions.length === 0 ? DASHBOARD_QUICK_ACTIONS_SEED : snap.quickActions;
  return { ...snap, quickActions };
}

export async function getDashboardSnapshot(
  params: GetDashboardSnapshotParams,
): Promise<DashboardSnapshot> {
  const snap = await bizGet<BackendSnapshot>('/biz/dashboard/snapshot', {
    params: { branchId: params.branchId, period: params.period },
  });
  return adaptSnapshot(snap);
}

/**
 * Fire-and-forget acknowledgement when a user dismisses an AI insight.
 *
 * Live wire today: the AI insight engine has no persistence (Phase H).
 * We resolve immediately and log dev-mode warnings so QA notices the gap.
 * The mock signature is preserved for caller compatibility.
 */
export async function dismissDashboardInsight(insightId: string): Promise<void> {
  if (!insightId) {
    throw new Error('insightId is required');
  }
  // No-op on the live wire — Phase H ships the POST endpoint.
}
